import React from 'react';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';

import { graphql } from 'gatsby';

import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import { Cards, Image, Paragraph, TitleLink, TitleSecond, TitleThird } from 'common/components';
import { IArticle } from 'common/interfaces/article.interface';
import { IParagraphCalloutStripe } from 'common/interfaces/landing.interface';

import { CopyImageAccordion } from 'components/Accordion/CopyImageAccordion';
import { ArticleSummary } from 'components/ArticleSummary/ArticleSummary';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { CalloutStripe } from 'components/CalloutStripe/CalloutStripe';
import { ContainerBackground } from 'components/Containers/ContainerBackground';
import { ContainerDefault } from 'components/Containers/ContainerDefault';
import { IconTitleColumns } from 'components/IconTitleColumns/IconTitleColumns';
import { LandingQuote } from 'components/LandingQuote/LandingQuote';
import { LayoutCom as Layout } from 'components/LayoutCom/LayoutCom';
import { List } from 'components/List/List';
import { Lockup } from 'components/Lockup/Lockup';
import { MoreAbout } from 'components/Navigation/MoreAbout';
import { SEO } from 'components/SEO/SEO';
import { PageType } from 'components/SEO/enum';
import type { AlternateLink, Article } from 'components/SEO/types';
import { SchemeStepProgress } from 'components/Schemes/SchemeStepProgress/SchemeStepProgress';
import { Sidemenu } from 'components/Sidemenu/Sidemenu';
import { SubTextImage } from 'components/SubTextImage/SubTextImage';
import { TableTopBorderedTitles } from 'components/Tables/TableTopBorderedTitles';
import { RowLandingTabs } from 'components/Tabs/RowLandingTabs';
import { TitleCopyColumns } from 'components/TitleCopy/TitleCopyColumns';
import { TitleCopyRows } from 'components/TitleCopy/TitleCopyRows';
import { TopBorderedColumnsGrid } from 'components/TopBorderedColumnsGrid/TopBorderedColumnsGrid';
import { TwoColumnsGrid } from 'components/TwoColumnsGrid/TwoColumnsGrid';
import { VideoPlayer } from 'components/VideoPlayer/VideoPlayer';

import { ThemeEnum } from 'enum/ThemeEnum';
import { getBlockItemsById, getContentBlockById } from 'helpers/LandingContentHelper';

import CoinsIcons from 'icons/coins.svg';
import CubePyramidIcon from 'icons/cube-pyramid.svg';
import HelpdeskIcon from 'icons/helpdesk.svg';
import LightningIcon from 'icons/lightning.svg';
import LikeIcon from 'icons/like.svg';
import SettingUpdate from 'icons/setting-update.svg';
import UploadIcon from 'icons/upload.svg';

import { ProviderScheme, TipsScheme } from './components';

import './dental-ehr.scss';

export const query = graphql`
  query DentalEhrPageQuery($field_id: String!, $langcode: String!) {
    nodeArticle(field_id: { eq: $field_id }, langcode: { eq: $langcode }) {
      title
      field_short_title
      date: field_creation_date
      changed
      path {
        alias
      }
      field_id
      langcode
      field_meta_tags {
        ...MetaTagsAll
      }
      relationships {
        ...ArticlesRelContAll
        ...ArticleDesktopHeaderImage
        ...ArticleMobileHeaderImage
        ...ArticleBreadcrumbs
        field_cluster_menu {
          ... on Node {
            ...RelatedLanding
          }
        }
        field_content {
          ...ParagraphContentBlock1
          ...BlockCalloutStripe
          ...ParagraphArticleVideo
        }
      }
    }
    links: allNodeArticle(filter: { field_id: { eq: $field_id } }) {
      nodes {
        langcode
        path {
          alias
        }
      }
    }
    locales: allLocale {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

interface Props {
  data: {
    nodeArticle: IArticle;
    links: {
      nodes: AlternateLink[];
    };
  };
}

enum DentalEhrBlockIds {
  Intro = 'intro',
  IntroLinks = 'intro_links',
  DentalMarket = 'dental_market',
  FeaturesDental = 'features_dental',
  CtaElevate = 'cta_elevate',
  Integrations = 'integrations',
  PremisesVsCloud = 'premises-vs-cloud',
  Tips = 'tips',
  Roadmap = 'roadmap',
  Solutions = 'solutions',
  SolutionsOpenDental = 'solutions_open_dental',
  SolutionsDentrix = 'solutions_dentrix',
  SolutionsCurveDental = 'solutions_curve_dental',
  SolutionsCustomDental = 'solutions_custom_dental',
  SolutionsMicrosoft = 'solutions_microsoft',
  CtaContactUs = 'cta_contact_us',
  Benefits = 'benefits',
  OvercomingChallenges = 'overcoming_challenges',
  Innovator = 'innovator',
}

const solutionsIcons = [
  <LightningIcon />,
  <CubePyramidIcon />,
  <UploadIcon />,
  <CoinsIcons />,
  <HelpdeskIcon />,
  <SettingUpdate />,
  <LikeIcon />,
];

const solutionsTabsIds = [
  DentalEhrBlockIds.SolutionsOpenDental,
  DentalEhrBlockIds.SolutionsDentrix,
  DentalEhrBlockIds.SolutionsCurveDental,
  DentalEhrBlockIds.SolutionsMicrosoft,
  DentalEhrBlockIds.SolutionsCustomDental,
];

export default function DentalEhr({ data }: Props) {
  const { title, date, relationships, field_meta_tags, changed, field_short_title, langcode } = data.nodeArticle;
  const {
    field_desktop_header_image,
    field_content,
    field_author: author,
    field_related_content,
    field_mobile_header_image,
    field_breadcrumbs,
    field_cluster_menu,
  } = relationships;
  const images = {
    desktop: field_desktop_header_image.localFile?.childImageSharp?.gatsbyImageData,
    mobile: field_mobile_header_image.localFile?.childImageSharp?.gatsbyImageData,
  };

  const { i18n } = useTranslation();

  if (i18n.language !== langcode) {
    i18n.changeLanguage(langcode);
  }

  const seoData: Article = {
    pageType: PageType.Article,
    path: data.nodeArticle.path.alias,
    title,
    image: field_desktop_header_image.localFile?.publicURL,
    author,
    metaDescription: field_meta_tags.description || '',
    breadcrumbs: field_breadcrumbs,
    followingAuthors: null,
    datePublished: date,
    dateModified: changed,
    articleCategory: relationships.field_category[0]?.name,
  };

  return (
    <Layout langcode={langcode} layoutClass="no-overflow" theme={ThemeEnum.Primary} alternateLinks={data.links.nodes}>
      <SEO<Article>
        data={seoData}
        metaTags={{ ...field_meta_tags, content_language: langcode }}
        alternateLinks={data.links.nodes}
      />

      <Lockup images={images} title={title} date={date} />

      <Breadcrumbs links={field_breadcrumbs} pageName={field_short_title!} />

      <div id="side-menu-scroll-container" className="dental-ehr">
        <ContainerDefault containerClass="has-side-menu breadcrumbs-margin section-margin-bottom-default">
          <TwoColumnsGrid
            rightClass="one-column-menu-small"
            leftContent={
              <>
                <Paragraph
                  item={ReactHtmlParser(
                    getContentBlockById(DentalEhrBlockIds.Intro, field_content).field_description.value,
                  )}
                  parseHtml
                />
                <ArticleSummary item={getBlockItemsById(DentalEhrBlockIds.Intro, field_content)[0]} />
              </>
            }
            rightContent={
              <Sidemenu
                items={getBlockItemsById(DentalEhrBlockIds.IntroLinks, field_content).map((item) => ({
                  ...item,
                  title: item.field_title,
                  link: item.field_id,
                }))}
                isLanding
                type="Article"
              />
            }
          />
        </ContainerDefault>

        <ContainerDefault containerClass="dental-ehr-market">
          <TitleSecond
            title={getContentBlockById(DentalEhrBlockIds.DentalMarket, field_content).field_title}
            titleClass="extra-text-margin"
          />

          <SubTextImage
            items={getBlockItemsById(DentalEhrBlockIds.DentalMarket, field_content)}
            percents={['67%', '13.5%', '$4.3']}
            percentSupTexts={['', '', 'billion']}
            subTextPosition={['', '', 'bottom']}
          />
        </ContainerDefault>

        <CopyImageAccordion
          id={DentalEhrBlockIds.FeaturesDental}
          containerClass="has-half-cta-inside cta-overhang"
          backgroundLightGray
          parseHtmlInTitleCopyRows
          imageName={getBlockItemsById(DentalEhrBlockIds.FeaturesDental, field_content)[0]}
          titleCopyRowsItems={[getContentBlockById(DentalEhrBlockIds.FeaturesDental, field_content)]}
          calloutItem={getContentBlockById<IParagraphCalloutStripe>(DentalEhrBlockIds.CtaElevate, field_content)}
          accordionItems={getBlockItemsById(DentalEhrBlockIds.FeaturesDental, field_content)
            .slice(1)
            .map((item) => ({
              title: item.field_title,
              link: item.field_link,
              content: (
                <>
                  <Paragraph parseHtml item={ReactHtmlParser(item.field_description.value)} />
                  <List items={item.field_list || []} columnsNumber={2} listClass="list-top-padding" />
                </>
              ),
            }))}
        />
        <ContainerDefault id={DentalEhrBlockIds.Integrations}>
          <TitleCopyColumns
            items={[getContentBlockById(DentalEhrBlockIds.Integrations, field_content)]}
            isBold={false}
            noPaddingTop
          />
          <TopBorderedColumnsGrid
            noBorderTop
            columnsNumber={4}
            items={getBlockItemsById(DentalEhrBlockIds.Integrations, field_content).map((item) => ({
              ...item,
              title: item.field_title,
              link: item.field_link,
              content: <Paragraph item={ReactHtmlParser(item.field_description.value)} parseHtml />,
            }))}
            titleClass="fifth-level-title"
          />
        </ContainerDefault>

        <ContainerBackground id={DentalEhrBlockIds.PremisesVsCloud} containerClass="background-light-gray ">
          <TitleCopyColumns
            items={[getContentBlockById(DentalEhrBlockIds.PremisesVsCloud, field_content)]}
            isBold={false}
            noPaddingTop
          />

          <TableTopBorderedTitles
            tableTopBorderedClass="dental-ehr-premises"
            tableClass="table-top-bordered-columns-three"
            titles={getBlockItemsById(DentalEhrBlockIds.PremisesVsCloud, field_content)[0].field_list}
            items={getBlockItemsById(DentalEhrBlockIds.PremisesVsCloud, field_content)
              .slice(1)
              .map((item) => ({
                field_title: item.field_title,
                field_list: item.field_list,
              }))}
          />
        </ContainerBackground>

        <ContainerDefault id={DentalEhrBlockIds.Tips} containerClass="dental-ehr-tips">
          <TwoColumnsGrid
            leftContent={
              <TitleCopyRows
                paragraphClass="medium-bold"
                items={[getContentBlockById(DentalEhrBlockIds.Tips, field_content)]}
              />
            }
            rightContent={<TipsScheme />}
          />

          <TopBorderedColumnsGrid
            noBorderTop
            isCounter
            columnsNumber={3}
            items={getBlockItemsById(DentalEhrBlockIds.Tips, field_content).map((item) => ({
              ...item,
              title: item.field_title,
              link: item.field_link,
              content: <Paragraph item={ReactHtmlParser(item.field_description.value)} parseHtml />,
            }))}
            titleClass="fifth-level-title"
          />
        </ContainerDefault>

        <ContainerBackground
          id={DentalEhrBlockIds.Roadmap}
          containerClass="background-theme section-padding-bottom-large"
        >
          <TitleCopyColumns items={[getContentBlockById(DentalEhrBlockIds.Roadmap, field_content)]} noPaddingTop />

          <SchemeStepProgress
            parseHTML
            isOutstanding={false}
            data={getBlockItemsById(DentalEhrBlockIds.Roadmap, field_content).map((item, index) => ({
              field_title: item.field_title,
              field_number: (index + 1).toString(),
              field_description: [item.field_description.value],
            }))}
          />
        </ContainerBackground>

        <ContainerDefault id={DentalEhrBlockIds.Solutions} containerClass="has-cta-after">
          <TitleSecond
            title={getContentBlockById(DentalEhrBlockIds.Solutions, field_content).field_title}
            titleClass="extra-text-margin"
          />

          <RowLandingTabs
            tabsClass="dental-ehr-solutions"
            tabsItem={solutionsTabsIds.map((item, index) => ({
              tabNav: getContentBlockById(item, field_content).field_title,
              optionalContent: (
                <>
                  {index < 4 && (
                    <>
                      <TwoColumnsGrid
                        containerClass={`title-image-container ${index === 3 ? 'microsoft-tab' : ''}`}
                        leftClass="hide-br"
                        leftContent={
                          index !== 3 ? (
                            <TitleCopyRows
                              items={[getContentBlockById(item, field_content)]}
                              parseHTML
                              titleClass="third-level-title"
                              paragraphClass="has-margin-right"
                            />
                          ) : (
                            <TitleLink
                              title={getContentBlockById(item, field_content).field_title}
                              link={getBlockItemsById(item, field_content)[0].field_link}
                            />
                          )
                        }
                        rightContent={
                          <>
                            {index === 1 && (
                              <VideoPlayer key={uuidv4()} node={getBlockItemsById(item, field_content)[0]} />
                            )}
                            {(index === 0 || index === 2) && (
                              <Image
                                image={
                                  getBlockItemsById(item, field_content)[0].relationships.field_image.localFile
                                    ?.childImageSharp.gatsbyImageData
                                }
                                alt={getBlockItemsById(item, field_content)[0].field_image?.alt}
                                className="image-border-small image-border-gray"
                              />
                            )}
                            {index === 3 && (
                              <Paragraph item={getContentBlockById(item, field_content).field_description.value} />
                            )}
                          </>
                        }
                      />
                      <IconTitleColumns
                        iconTitleColumnsClass="large-gap"
                        items={getBlockItemsById(item, field_content)
                          .slice(1)
                          .map((items, i) => ({
                            title: items.field_title,
                            icon: solutionsIcons[i],
                            text: items.field_description.value,
                            parseHTML: true,
                          }))}
                      />
                    </>
                  )}
                  {index === 4 && (
                    <TitleCopyRows
                      items={[getContentBlockById(item, field_content)]}
                      parseHTML
                      titleClass="third-level-title"
                    />
                  )}
                </>
              ),
            }))}
          />
        </ContainerDefault>

        <CalloutStripe item={getContentBlockById(DentalEhrBlockIds.CtaContactUs, field_content)} />

        <ContainerBackground
          id={DentalEhrBlockIds.Benefits}
          containerClass="dental-ehr-benefits has-no-margin-bottom hide-br"
        >
          <TitleCopyColumns items={[getContentBlockById(DentalEhrBlockIds.Benefits, field_content)]} noPaddingTop />

          <RowLandingTabs
            tabsClass="dental-ehr-benefits"
            tabsItem={getBlockItemsById(DentalEhrBlockIds.Benefits, field_content)
              .slice(0, 5)
              .map((item, index) => ({
                tabNav: item.field_title,
                optionalContent: (
                  <>
                    <TitleThird title={item.field_title} />
                    {index !== 2 && index !== 3 && (
                      <TwoColumnsGrid
                        rightClass={classNames({ 'desktop-tablet-only': index === 0 })}
                        leftContent={
                          <div
                            className={index === 0 ? '' : 'paragraph'}
                            dangerouslySetInnerHTML={{ __html: item.field_description.value }}
                          />
                        }
                        rightContent={
                          <>
                            {index === 0 && <ProviderScheme />}
                            {index === 1 && (
                              <LandingQuote
                                quote={getBlockItemsById(DentalEhrBlockIds.Benefits, field_content)[5]}
                                noBackground
                              />
                            )}
                            {index === 4 && (
                              <LandingQuote
                                quote={getBlockItemsById(DentalEhrBlockIds.Benefits, field_content)[6]}
                                noBackground
                              />
                            )}
                          </>
                        }
                      />
                    )}
                    {(index === 2 || index === 3) && (
                      <Paragraph item={ReactHtmlParser(item.field_description.value)} parseHtml />
                    )}
                  </>
                ),
              }))}
            multiRow
          />
        </ContainerBackground>

        <ContainerBackground
          id={DentalEhrBlockIds.OvercomingChallenges}
          containerClass="background-light-gray dental-ehr-challenges-wrapper"
        >
          <TitleCopyColumns
            items={[getContentBlockById(DentalEhrBlockIds.OvercomingChallenges, field_content)]}
            isBold={false}
            noPaddingTop
            parseHtml
          />

          <TableTopBorderedTitles
            tableTopBorderedClass="dental-ehr-challenges"
            tableClass="table-top-bordered-columns-three"
            titles={getBlockItemsById(DentalEhrBlockIds.OvercomingChallenges, field_content)[0].field_list}
            items={getBlockItemsById(DentalEhrBlockIds.OvercomingChallenges, field_content)
              .slice(1)
              .map((item) => ({
                field_title: item.field_title,
                field_list: item.field_list,
              }))}
          />
        </ContainerBackground>

        <ContainerDefault id={DentalEhrBlockIds.Innovator} containerClass="">
          <TitleSecond title={getContentBlockById(DentalEhrBlockIds.Innovator, field_content).field_title} />

          <TwoColumnsGrid
            leftContent={
              <Paragraph
                item={getContentBlockById(DentalEhrBlockIds.Innovator, field_content).field_description.value}
              />
            }
            rightContent={
              <Paragraph
                item={getBlockItemsById(DentalEhrBlockIds.Innovator, field_content)[0].field_description.value}
              />
            }
          />
        </ContainerDefault>
      </div>

      <Cards items={field_related_content} />

      <MoreAbout langcode={langcode} id={field_cluster_menu!.field_id!} relatedContent={field_related_content} />
    </Layout>
  );
}
